import { CheckCircle2, Clock, DollarSign, ShoppingBag, XCircle } from 'lucide-react';

export interface DealItem {
  productId?: number;
  name: string;
  price: number;
  qty: number;
}

export interface PipelineStageItem {
  id: number;
  name: string;
  color?: string;
  type?: string;
  order: number;
}

export interface Pipeline {
  id: number;
  name: string;
  businessType: string;
  isDefault: boolean;
  stages: PipelineStageItem[];
}

export interface Deal {
  id: number;
  userId: number;
  conversationId?: number | null;
  pipelineId?: number | null;
  stageId?: number | null;
  assignedToId?: number | null;
  title: string;
  type?: string;
  amount: number;
  originalAmount?: number;
  discountPercent?: number;
  discountAmount?: number;
  status: 'open' | 'won' | 'lost' | string;
  stage: string;
  customData?: Record<string, any>;
  items: DealItem[];
  createdAt: string;
  stockAppliedAt?: string | null;
  conversation?: {
    id: number;
    contactName: string;
    contactPhone?: string;
    contactCity?: string;
    temp?: string;
    channel?: {
      type: string;
      name: string;
    };
  };
}

export interface StageItem {
  id: string;
  stageId?: number;
  type?: string;
  label: string;
  color: string;
  bg: string;
  icon: any;
}

/** Aggregates for the cards above the board, computed server-side. */
export interface DealSummary {
  totalCount: number;
  openCount: number;
  wonCount: number;
  lostCount: number;
  pipelineAmount: number;
  wonAmount: number;
  winRate: number;
  avgCheck: number;
}

/**
 * Filter state, one field per query parameter the API accepts. Everything is a
 * string except the ids, so the inputs can be bound directly and an empty string
 * means "not set" — those are dropped when the query is built.
 */
export interface DealFiltersState {
  q: string;
  pipelineId: number | null;
  stageId: number | null;
  status: 'all' | 'open' | 'won' | 'lost';
  assignedToId: 'all' | 'unassigned' | number;
  temp: 'all' | 'hot' | 'warm' | 'cold';
  amountMin: string;
  amountMax: string;
  dateFrom: string;
  dateTo: string;
  sort: 'createdAt' | 'amount' | 'title';
  order: 'asc' | 'desc';
}

export const DEFAULT_DEAL_FILTERS: DealFiltersState = {
  q: '',
  pipelineId: null,
  stageId: null,
  status: 'all',
  assignedToId: 'all',
  temp: 'all',
  amountMin: '',
  amountMax: '',
  dateFrom: '',
  dateTo: '',
  sort: 'createdAt',
  order: 'desc',
};

export const DEAL_PAGE_SIZE = 50;

export const FALLBACK_STAGES: StageItem[] = [
  { id: 'Новая заявка', type: 'open', label: 'Новая заявка', color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.08)', icon: Clock },
  { id: 'Согласован', type: 'open', label: 'Согласован', color: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.08)', icon: ShoppingBag },
  { id: 'Ожидает оплаты', type: 'open', label: 'Ожидает оплаты', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.08)', icon: DollarSign },
  { id: 'В доставке', type: 'open', label: 'В доставке', color: '#06b6d4', bg: 'rgba(6, 182, 212, 0.08)', icon: ShoppingBag },
  { id: 'Выполнен', type: 'won', label: 'Выполнен (Успех)', color: '#10b981', bg: 'rgba(16, 185, 129, 0.08)', icon: CheckCircle2 },
  { id: 'Отменен', type: 'lost', label: 'Отменен', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.08)', icon: XCircle },
];

export function stagesOfPipeline(pipeline: Pipeline | null): StageItem[] {
  if (!pipeline?.stages?.length) return FALLBACK_STAGES;
  return pipeline.stages.map((stage) => ({
    id: stage.name,
    stageId: stage.id,
    type: stage.type,
    label: stage.name,
    color: stage.color || '#6d5ef3',
    bg: `${stage.color || '#6d5ef3'}14`,
    icon: stage.type === 'won' ? CheckCircle2 : stage.type === 'lost' ? XCircle : Clock,
  }));
}

export function isDealInStage(deal: Deal, stage: StageItem, stageIndex: number, stages: StageItem[]): boolean {
  // Persisted stage IDs are canonical and must never be combined with a name match.
  if (deal.stageId && stages.some((candidate) => candidate.stageId)) {
    return deal.stageId === stage.stageId;
  }

  // Legacy deals without a stageId fall back to their stored stage name.
  if (deal.stage && deal.stage.trim().toLowerCase() === stage.id.trim().toLowerCase()) {
    return true;
  }

  const matchesAnyStage = stages.some((candidate) =>
    deal.stage && candidate.id && deal.stage.trim().toLowerCase() === candidate.id.trim().toLowerCase(),
  );

  // Keep unmapped legacy deals visible in the first column.
  return !matchesAnyStage && stageIndex === 0;
}

export function statusForStage(stage: StageItem | undefined): 'open' | 'won' | 'lost' {
  if (stage?.type === 'won') return 'won';
  if (stage?.type === 'lost') return 'lost';
  return 'open';
}

/**
 * Serialise the filter state into the query the API expects.
 *
 * Empty and neutral values are dropped rather than sent blank: the zod schema
 * coerces, so `stageId=` would become 0 and be rejected, and `dateFrom=` would be
 * an invalid date. Omitting them lets the schema's defaults apply.
 */
export function buildDealsQuery(
  filters: DealFiltersState,
  options: { page?: number; limit?: number; paginated?: boolean } = {},
): string {
  const params = new URLSearchParams();

  const q = filters.q.trim();
  if (q) params.set('q', q);
  if (filters.pipelineId) params.set('pipelineId', String(filters.pipelineId));
  if (filters.stageId) params.set('stageId', String(filters.stageId));
  if (filters.status !== 'all') params.set('status', filters.status);
  if (filters.assignedToId !== 'all') params.set('assignedToId', String(filters.assignedToId));
  if (filters.temp !== 'all') params.set('temp', filters.temp);
  if (filters.amountMin.trim() !== '') params.set('amountMin', filters.amountMin.trim());
  if (filters.amountMax.trim() !== '') params.set('amountMax', filters.amountMax.trim());
  // `<input type="date">` gives a bare calendar day, which is ambiguous the
  // moment the browser and the server sit in different time zones. Resolve it
  // here, where the operator's own offset is known, and send a real instant:
  // the start and the end of the chosen day as they mean it.
  if (filters.dateFrom) params.set('dateFrom', localDayStart(filters.dateFrom));
  if (filters.dateTo) params.set('dateTo', localDayEnd(filters.dateTo));

  if (options.paginated) {
    params.set('paginated', 'true');
    params.set('sort', filters.sort);
    params.set('order', filters.order);
    params.set('page', String(options.page || 1));
    params.set('limit', String(options.limit || DEAL_PAGE_SIZE));
  }

  return params.toString();
}

/** `YYYY-MM-DD` → the instant that day starts in the operator's time zone. */
function localDayStart(day: string): string {
  return new Date(`${day}T00:00:00.000`).toISOString();
}

/** `YYYY-MM-DD` → the last instant of that day in the operator's time zone. */
function localDayEnd(day: string): string {
  return new Date(`${day}T23:59:59.999`).toISOString();
}

export function hasActiveDealFilters(filters: DealFiltersState): boolean {
  return Boolean(
    filters.q.trim() ||
    filters.stageId ||
    filters.status !== 'all' ||
    filters.assignedToId !== 'all' ||
    filters.temp !== 'all' ||
    filters.amountMin.trim() ||
    filters.amountMax.trim() ||
    filters.dateFrom ||
    filters.dateTo,
  );
}

export interface ChannelBadge {
  label: string;
  bg: string;
  color: string;
}

export function channelBadgeOf(deal: Deal): ChannelBadge {
  switch (deal.conversation?.channel?.type || 'widget') {
    case 'telegram':
      return { label: 'Telegram', bg: '#e0f2fe', color: '#0284c7' };
    case 'whatsapp':
      return { label: 'WhatsApp', bg: '#dcfce7', color: '#16a34a' };
    case 'vk':
      return { label: 'ВКонтакте', bg: '#ede9fe', color: '#7c3aed' };
    default:
      return { label: 'Виджет', bg: '#eff6ff', color: '#2563eb' };
  }
}

export interface AssigneeOption {
  id: number;
  name: string;
}
